import { useCallback, useState, type FormEvent } from 'react';
import { QrScannerView } from '@/features/scanner/components/QrScannerView';
import { ScanSuccessOverlay } from '@/features/scanner/components/ScanSuccessOverlay';
import { ScanControls } from '@/features/scanner/components/ScanControls';
import { useDuplicateGuard } from '@/features/scanner/hooks/useDuplicateGuard';
import { scanFeedback } from '@/features/scanner/services/feedbackService';
import { delegateLookupApi } from '@/features/scanner/services/delegateLookupApi';
import { scanCaptureService, MissingExhibitorContextError } from '@/features/scanner/services/scanCaptureService';
import { useAuth } from '@/app/providers/AuthProvider';
import { Button } from '@/shared/components/Button';
import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus';
import type { DelegatePublicInfo } from '@/shared/types/domain';
import './ScannerPage.css';

// Spec requirement: ignore repeat reads of the identical QR value within 3
// seconds, so a badge that lingers in the camera's view across several
// decode frames doesn't register as multiple scans.
const IMMEDIATE_REREAD_GUARD_MS = 3000;

export function ScannerPage() {
  const { user } = useAuth();
  const [mode, setMode] = useState<'camera' | 'manual'>('camera');
  const [manualValue, setManualValue] = useState('');
  const [captured, setCaptured] = useState<string | null>(null);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [preview, setPreview] = useState<DelegatePublicInfo | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const isOnline = useOnlineStatus();

  const shouldAllowScan = useDuplicateGuard(IMMEDIATE_REREAD_GUARD_MS);

  /**
   * The single entry point for "a badge value was captured", used by BOTH
   * the camera decode callback and manual entry -- per the spec, manual
   * entry is a legitimate scan-capture fallback (e.g. a damaged badge or
   * denied camera permission), not merely a lookup tool.
   *
   * Follows the exact required workflow: save locally FIRST (awaited, but
   * near-instant -- a single IndexedDB write, no network involved), THEN
   * fire feedback and the success confirmation, THEN return to a ready
   * state. The delegate-info preview fetched afterwards is a best-effort
   * convenience on top -- if it fails (e.g. offline), the capture itself
   * (already durably saved and queued) is entirely unaffected.
   */
  const handleScanCaptured = useCallback(
    async (rawValue: string) => {
      const trimmed = rawValue.trim();
      if (!trimmed || !shouldAllowScan(trimmed) || !user) {
        return;
      }

      setCaptureError(null);

      try {
        await scanCaptureService.captureScan({ scannedValue: trimmed, userId: user.userId });
      } catch (err) {
        // A failed local save is the one truly exceptional case in this
        // flow -- it means the scan was NOT captured, so no success
        // feedback should fire at all.
        setCaptureError(
          err instanceof MissingExhibitorContextError
            ? err.message
            : 'Could not save this scan on your device. Please try again.'
        );
        return;
      }

      scanFeedback.success();
      setCaptured(trimmed);
      setPreview(null);
      setPreviewError(null);

      if (isOnline) {
        delegateLookupApi
          .lookup(trimmed)
          .then(setPreview)
          .catch(() => setPreviewError('Preview unavailable — the lead was still captured.'));
      }
    },
    [shouldAllowScan, isOnline, user]
  );

  function handleManualSubmit(event: FormEvent) {
    event.preventDefault();
    if (!manualValue.trim()) return;
    void handleScanCaptured(manualValue);
    setManualValue('');
  }

  return (
    <div>
      {mode === 'camera' ? (
        <QrScannerView enabled={mode === 'camera'} onDecode={(v) => void handleScanCaptured(v)} />
      ) : (
        <form onSubmit={handleManualSubmit} style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
          <input
            className="field__input"
            style={{ flex: 1 }}
            placeholder="Enter badge ID"
            value={manualValue}
            onChange={(e) => setManualValue(e.target.value)}
            aria-label="Badge ID"
            autoFocus
          />
          <Button type="submit">Capture</Button>
        </form>
      )}

      <ScanControls mode={mode} onModeChange={setMode} />

      {captureError && (
        <p role="alert" style={{ textAlign: 'center', color: 'var(--danger)', fontSize: '0.875rem' }}>
          {captureError}
        </p>
      )}

      {captured && !preview && !previewError && (
        <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          {isOnline ? 'Loading lead details…' : 'Offline — details will sync once you\u2019re back online.'}
        </p>
      )}

      {preview && (
        <div className="scanner-page__result">
          {preview.consentGiven ? (
            <>
              <h3 style={{ margin: '0 0 4px' }}>
                {preview.firstName} {preview.lastName}
              </h3>
              <p style={{ margin: '2px 0', color: 'var(--text-muted)' }}>
                {preview.jobTitle} at {preview.company}
              </p>
              <p style={{ margin: '2px 0', color: 'var(--text-muted)' }}>{preview.email}</p>
            </>
          ) : (
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>{preview.message}</p>
          )}
        </div>
      )}

      {previewError && (
        <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>{previewError}</p>
      )}

      {captured && <ScanSuccessOverlay scannedValue={captured} onDismiss={() => setCaptured(null)} />}
    </div>
  );
}
